import { useQuery } from '@tanstack/react-query'

import { graphql_multi_call } from '../../schemas/server_admin_query.ts'

import { query_asset_prices } from './fund_graphql.ts'
import { keys } from './keystore.ts'

export interface AssetPrice {
  date: string
  price_per_share: number
  source: string
  asset: {
    id: string
  }
}

export interface AssetPricesConnection {
  nodes: AssetPrice[]
  pageInfo: {
    hasNextPage: boolean
    hasPreviousPage: boolean
    startCursor: string | null
    endCursor: string | null
  }
  total_count: number
}

export interface AssetPricesResponse {
  asset_prices: AssetPricesConnection
}

export interface AssetPricesQueryVariables {
  first?: number | null
  after?: string | null
  last?: number | null
  before?: string | null
  start_date?: string | null
  end_date?: string | null
  category?: string | null
}

export interface UseAssetPricesOptions {
  onProgress?: (loadedCount: number, totalCount?: number) => void
}

export function useAssetPrices(options?: UseAssetPricesOptions) {
  const pageSize = 2000
  return useQuery({
    queryKey: keys.admin_funds.assetPrices.queryKey,
    // eslint-disable-next-line complexity
    queryFn: async (): Promise<AssetPricesResponse | null> => {
      try {
        const allNodes: AssetPrice[] = []
        let hasNextPage = true
        let after: string | null = null
        const pageInfo = {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: null as string | null,
          endCursor: null as string | null,
        }
        let totalCount = 0

        while (hasNextPage) {
          const response = (await graphql_multi_call({
            query: query_asset_prices,
            variables: {
              after,
              first: pageSize,
            },
          })) as AssetPricesResponse | undefined

          if (!response) break

          const {
            nodes,
            pageInfo: currentPageInfo,
            total_count,
          } = response.asset_prices

          allNodes.push(...nodes)
          hasNextPage = currentPageInfo.hasNextPage
          after = currentPageInfo.endCursor
          totalCount = total_count

          if (!pageInfo.startCursor) {
            pageInfo.startCursor = currentPageInfo.startCursor
          }

          pageInfo.endCursor = currentPageInfo.endCursor
          pageInfo.hasPreviousPage = currentPageInfo.hasPreviousPage

          options?.onProgress?.(allNodes.length, totalCount)
        }

        return {
          asset_prices: {
            nodes: allNodes,
            pageInfo: {
              ...pageInfo,
              hasNextPage: false,
            },
            total_count: totalCount,
          },
        }
      } catch (error) {
        console.error('Error fetching asset prices:', error)
        return null
      }
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  })
}
